import axios, { type InternalAxiosRequestConfig } from 'axios'

import { config } from '../config'
import { attachMicroserviceAuthRetry } from '../sso/interceptors/microservice-auth-retry.interceptor'
import { resolveAppAccessToken } from '../sso/services/app-access.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { logWarn } from '../sso/utils/sso-logger'

/**
 * App-data / app-queries API client with automatic auth.
 * SSO mode → Pay RS256 access token (bridged from Identity via /auth/request-access-token).
 */
export const AppDataHttp = axios.create({
  baseURL: config.api.appData.baseUrl,
  withCredentials: false
})

AppDataHttp.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
  if (isSuiteSsoRedirectMode()) {
    try {
      const payAccessToken = await resolveAppAccessToken()
      requestConfig.headers.Authorization = `Bearer ${payAccessToken}`
      return requestConfig
    } catch (error) {
      logWarn('AppDataHttp — Pay auth bridge failed', { error })
      throw error
    }
  }

  const hasExplicitAuth =
    typeof requestConfig.headers?.Authorization === 'string' &&
    requestConfig.headers.Authorization.length > 0

  if (hasExplicitAuth) {
    return requestConfig
  }

  try {
    const accessToken = await resolveAppAccessToken()
    requestConfig.headers.Authorization = `Bearer ${accessToken}`
  } catch (error) {
    logWarn('AppDataHttp — could not resolve app access token', { error })
    throw error
  }

  return requestConfig
})

attachMicroserviceAuthRetry(AppDataHttp)
